/**
 * Confirm Dialog Component
 * Reusable confirmation dialog for destructive actions
 * Supports keyboard navigation (Enter to confirm, Escape to cancel)
 */

import React, { useCallback, useRef, useEffect } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { Warning as WarningIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ResponsiveDialog } from "./ResponsiveDialog";

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message. Pass a string for plain text (newlines preserved
   *  via `white-space: pre-line`) or a ReactNode for richer content. */
  message: React.ReactNode;
  /** Confirm button text */
  confirmLabel?: string;
  /** Cancel button text */
  cancelLabel?: string;
  /** Confirm button color */
  confirmColor?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  /** Whether the action is in progress */
  isLoading?: boolean;
  /** Disable the confirm control when the action would do nothing. */
  confirmDisabled?: boolean;
  /** Whether this is a destructive action */
  destructive?: boolean;
  /** Enter confirms (the default, with an on-screen hint). Pass false for an
   *  irreversible action: Enter then only presses the focused button -- the
   *  auto-focused Cancel cancels -- and confirming takes a deliberate click
   *  or Tab to the confirm button. */
  enterConfirms?: boolean;
  /** Called when confirmed */
  onConfirm: () => void;
  /** Called when canceled or closed */
  onCancel: () => void;
  /** Called once the dialog has finished closing (its exit transition):
   *  the moment to move focus somewhere that is still on the page. */
  onExited?: () => void;
  /** Don't hand focus back to the opener on close (the caller moves it in
   *  onExited, e.g. because the opener is gone). */
  disableRestoreFocus?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmColor = "primary",
  isLoading = false,
  confirmDisabled = false,
  destructive = false,
  enterConfirms = true,
  onConfirm,
  onCancel,
  onExited,
  disableRestoreFocus = false,
}) => {
  const color = destructive ? "error" : confirmColor;
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus cancel button when dialog opens for safety (especially for destructive actions)
  // Enter key still triggers confirm via the onKeyDown handler
  useEffect(() => {
    if (open && cancelButtonRef.current) {
      const timer = setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Enter confirms (matching the on-screen hint), EXCEPT when a form field is
  // focused — pressing Enter in an input/textarea/select should do its native
  // thing, not silently trigger the confirm. Cancel stays auto-focused for
  // safety, but the explicit handler keeps Enter = confirm as users expect.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "Enter" || !enterConfirms || isLoading || confirmDisabled) return;
      const tag = (event.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
      onConfirm();
    },
    [enterConfirms, isLoading, confirmDisabled, onConfirm]
  );

  return (
    <ResponsiveDialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      onKeyDown={handleKeyDown}
      slotProps={onExited ? { transition: { onExited } } : undefined}
      disableRestoreFocus={disableRestoreFocus}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {destructive && <WarningIcon color="error" />}
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="confirm-dialog-description"
          // Rich content (checkboxes, alerts) can't sit inside a <p>.
          component={typeof message === "string" ? "p" : "div"}
          sx={{ whiteSpace: "pre-line" }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        {/* Keyboard hint */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: { xs: "none", sm: "block" }
          }}>
          {enterConfirms ? "Press Enter to confirm, Esc to cancel" : "Esc to cancel"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button ref={cancelButtonRef} onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            color={color}
            variant="contained"
            disabled={isLoading || confirmDisabled}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : destructive ? (
                <DeleteIcon />
              ) : undefined
            }
          >
            {isLoading ? "Processing..." : confirmLabel}
          </Button>
        </Box>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ConfirmDialog;
